import { useState, useCallback, useEffect, useRef } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { realtimeCentral } from '@/core/RealtimeCentral';
import { debugLogger } from '@/utils/debugLogger';
import { ProfileData } from '@/types/profile';
import { FeedProfile, transformFeedProfileToProfileData } from '@/utils/feedProfileTransform';
import { AdminService } from '@/core/services/AdminService';
import { useProfile } from '@/contexts/ProfileContext';
import { supabase } from '@/integrations/supabase/client';

interface UserProfile {
  id: string;
  role: string;
  gender: string;
  orientation: string;
  latitude: number;
  longitude: number;
  onboarding_completed: boolean;
}

interface UseSimpleFeedProps {
  onLoadComplete?: (onboardingStatus: { completed: boolean; current_step: number }) => void;
}

export const useSimpleFeed = ({ onLoadComplete }: UseSimpleFeedProps = {}) => {
  const { user } = useAuth();
  const { profile: profileData, loading: profileLoading } = useProfile();
  const [profiles, setProfiles] = useState<FeedProfile[]>([]);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [connectionStatuses, setConnectionStatuses] = useState<Record<string, string>>({});
  const [hasMore, setHasMore] = useState(true);
  const [loadedCount, setLoadedCount] = useState(20);
  const [hasFetched, setHasFetched] = useState(false); // Track if initial fetch was attempted

  const INITIAL_LOAD = 20;
  const LOAD_MORE_BATCH = 20;

  // ✅ FASE 3: Log de diagnóstico do estado do hook
  useEffect(() => {
    debugLogger.debug('SimpleFeed', 'Hook state:', {
      hasUser: !!user?.id,
      userId: user?.id,
      hasProfileData: !!profileData,
      profileId: profileData?.id,
      loading,
      error,
      profilesCount: profiles.length
    });
  }, [user?.id, profileData, loading, error, profiles.length]);

  // Stabilized callback - depends on user?.id and profileData
  const fetchFeedData = useCallback(async () => {
    debugLogger.debug('SimpleFeed', 'fetchFeedData called:', {
      hasUser: !!user?.id,
      hasProfileData: !!profileData,
      willFetch: !!(user?.id && profileData)
    });
    
    if (!user?.id) {
      debugLogger.warn('SimpleFeed', 'Cannot fetch feed - no user');
      return;
    }

    // Se ProfileContext ainda está carregando, aguardar
    if (profileLoading) {
      debugLogger.debug('SimpleFeed', 'Waiting for ProfileContext to load...');
      return;
    }

    // Fallback: Se profileData não vier do contexto após carregamento, buscar direto
    let effectiveProfileData = profileData;
    if (!effectiveProfileData) {
      debugLogger.warn('SimpleFeed', 'ProfileContext empty after loading, fetching profile directly...');
      try {
        const { data: directProfile, error: profileError } = await supabase
          .from('profiles')
          .select('id, role, gender, orientation, latitude, longitude, onboarding_completed')
          .eq('id', user.id)
          .single();
        
        if (profileError) throw profileError;
        
        if (directProfile) {
          debugLogger.debug('SimpleFeed', 'Profile fetched directly:', directProfile);
          effectiveProfileData = directProfile as any;
        } else {
          throw new Error('Profile not found');
        }
      } catch (err) {
        debugLogger.error('SimpleFeed', 'Failed to fetch profile directly:', err);
        setError('Erro ao carregar dados do perfil');
        setLoading(false);
        return;
      }
    }

    try {
      setLoading(true);
      setError(null);

      setUserProfile({
        id: effectiveProfileData.id,
        role: effectiveProfileData.role,
        gender: effectiveProfileData.gender,
        orientation: effectiveProfileData.orientation,
        latitude: (effectiveProfileData as any).latitude,
        longitude: (effectiveProfileData as any).longitude,
        onboarding_completed: effectiveProfileData.onboarding_completed || false
      });

      // Call onLoadComplete if provided
      if (onLoadComplete) {
        onLoadComplete({
          completed: effectiveProfileData.onboarding_completed || false,
          current_step: (effectiveProfileData as any).last_onboarding_step || 0
        });
      }

      // Determinar se deve excluir perfis de teste
      // - Usuários comuns: sempre exclui
      // - Admins: respeita configuração admin_show_test_profiles do ControlPanel
      let excludeTestProfiles = true; // Default: sempre excluir
      
      const isAdmin = await AdminService.isAdmin(user.id);
      if (isAdmin) {
        const { data: setting } = await supabase
          .from('app_settings')
          .select('value')
          .eq('key', 'admin_show_test_profiles')
          .single();
        
        const showTestProfiles = setting?.value === true;
        excludeTestProfiles = !showTestProfiles;
        
        debugLogger.debug('SimpleFeed', 'Admin test profile config:', {
          isAdmin,
          showTestProfiles,
          excludeTestProfiles
        });
      }

      // Get feed data - parameter order must match SQL function signature
      const { data, error: feedError } = await supabase.rpc('get_feed_data', {
        current_user_id: user.id,
        user_latitude: effectiveProfileData.latitude ?? 0,
        user_longitude: effectiveProfileData.longitude ?? 0,
        user_role: effectiveProfileData.role,
        user_orientation: effectiveProfileData.orientation,
        user_gender: effectiveProfileData.gender,
        exclude_test_profiles: excludeTestProfiles
      }) as { data: FeedProfile[] | null; error: any };

      if (feedError) throw feedError;

      const allProfiles = data || [];
      setProfiles(allProfiles);
      setHasMore(allProfiles.length > INITIAL_LOAD);
      
      // Temporary diagnostic log
      debugLogger.debug('SimpleFeed', 'Sample profiles from RPC:', allProfiles.slice(0, 3).map(p => ({
        profile_id: p.profile_id,
        username: p.username,
        height: p.height,
        body_type: p.body_type,
        marital_status: p.marital_status
      })));
      
      // Initialize connection statuses
      const statuses: Record<string, string> = {};
      allProfiles.forEach((profile) => {
        statuses[profile.profile_id] = profile.connection_status;
      });
      setConnectionStatuses(statuses);

      // Pré-popular cache do AdminService com roles retornados pelo get_feed_data
      AdminService.preloadRolesCache(
        allProfiles.map(p => ({
          id: p.profile_id,
          user_role: p.profile_user_role || 'user'
        }))
      );

      debugLogger.debug('SimpleFeed', 'Feed data loaded:', {
        total: allProfiles.length,
        initialLoad: INITIAL_LOAD,
        hasMore: allProfiles.length > INITIAL_LOAD,
        testProfilesFiltered: excludeTestProfiles,
        adminCachePreloaded: allProfiles.length
      });
    } catch (err) {
      debugLogger.error('SimpleFeed', 'Error fetching feed:', err);
      const errorMessage = err instanceof Error ? err.message : 'Erro ao carregar feed';
      setError(errorMessage);
      
      // Don't force logout on feed errors - just show empty state
      // Users can still access other parts of the app
      debugLogger.warn('SimpleFeed', 'Feed failed to load, displaying empty state');
    } finally {
      setLoading(false);
    }
  }, [user?.id, profileData, profileLoading, onLoadComplete]);

  // Update connection status in local state when realtime events occur
  const updateConnectionStatus = useCallback((profileId: string, status: string) => {
    setConnectionStatuses(prev => ({
      ...prev,
      [profileId]: status
    }));

    setProfiles(currentProfiles => 
      currentProfiles.map(profile => 
        profile.profile_id === profileId 
          ? { ...profile, connection_status: status }
          : profile
      )
    );
  }, []);

  const getConnectionStatus = useCallback((profileId: string): 'none' | 'sent' | 'received' | 'matched' => {
    const status = connectionStatuses[profileId] || 'none';
    return status as 'none' | 'sent' | 'received' | 'matched';
  }, [connectionStatuses]);

  // Return profiles with pagination - only return loaded count
  const getProfiles = useCallback((): ProfileData[] => {
    const visibleProfiles = profiles.slice(0, loadedCount);
    return visibleProfiles.map(transformFeedProfileToProfileData);
  }, [profiles, loadedCount]);

  // Reset and refetch - smooth refresh without page reload
  const resetAndRefetch = useCallback(async () => {
    debugLogger.debug('SimpleFeed', 'Resetting and refetching feed...');
    
    // Clear current state
    setProfiles([]);
    setConnectionStatuses({});
    setLoadedCount(INITIAL_LOAD);
    setHasMore(true);
    setLoading(true);
    setError(null);
    
    // Fetch fresh data
    await fetchFeedData();
  }, [fetchFeedData]);

  // Load more profiles
  const loadMore = useCallback(() => {
    if (loadedCount >= profiles.length) {
      setHasMore(false);
      debugLogger.debug('SimpleFeed', 'All profiles loaded');
      return;
    }

    const newCount = Math.min(loadedCount + LOAD_MORE_BATCH, profiles.length);
    setLoadedCount(newCount);
    setHasMore(newCount < profiles.length);
    
    debugLogger.debug('SimpleFeed', 'Loading more profiles:', {
      previous: loadedCount,
      new: newCount,
      total: profiles.length,
      hasMore: newCount < profiles.length
    });
  }, [loadedCount, profiles.length, LOAD_MORE_BATCH]);

  // Stabilized callback - ONLY depends on user?.id
  const handleConnectionChanged = useCallback((connectionData: any) => {
    debugLogger.debug('SimpleFeed', 'Updating connection status from realtime:', connectionData);
    
    const profileId = connectionData.new?.user_id === user?.id 
      ? connectionData.new?.target_id 
      : connectionData.new?.user_id;
    
    if (profileId) {
      const newStatus = connectionData.eventType === 'DELETE' 
        ? 'none' 
        : (connectionData.new?.status || 'none');
      updateConnectionStatus(profileId, newStatus);
    }
  }, [user?.id, updateConnectionStatus]);

  // Ref para garantir fetchFeedData mais recente
  const fetchFeedDataRef = useRef(fetchFeedData);
  useEffect(() => {
    fetchFeedDataRef.current = fetchFeedData;
  }, [fetchFeedData]);

  // Main effect - tracks user?.id and profileLoading
  useEffect(() => {
    if (!user?.id) return;

    // Aguardar ProfileContext terminar de carregar antes de buscar feed
    if (profileLoading) {
      debugLogger.debug('SimpleFeed', 'Waiting for ProfileContext to finish loading...');
      return;
    }

    // Initialize RealtimeCentral
    realtimeCentral.initialize(user.id);

    // Subscribe to connection changes
    const unsubscribe = realtimeCentral.subscribe(`feed-${user.id}`, {
      id: `feed-${user.id}`,
      onConnectionChanged: handleConnectionChanged
    });

    // Fetch initial data
    debugLogger.debug('SimpleFeed', 'ProfileContext ready, fetching feed data');
    fetchFeedDataRef.current();
    setHasFetched(true);

    return () => {
      unsubscribe();
    };
  }, [user?.id, profileLoading, handleConnectionChanged]); // Added profileLoading dependency

  // Bug Fix: Re-trigger fetch when profileData becomes available after initial mount
  useEffect(() => {
    // Se já tentou fetch, profileContext não está mais carregando, 
    // mas ainda está em loading sem dados, tentar novamente
    if (hasFetched && !profileLoading && loading && profiles.length === 0 && user?.id && profileData) {
      debugLogger.debug('SimpleFeed', 'ProfileData now available, retrying fetch...');
      fetchFeedDataRef.current();
    }
  }, [hasFetched, profileLoading, loading, profiles.length, user?.id, profileData]);

  // Watchdog ativo: Retry automático após 10s de carregamento
  useEffect(() => {
    if (!loading || profiles.length > 0) return;

    const loadingTimeoutId = setTimeout(() => {
      if (loading && profiles.length === 0 && user?.id) {
        debugLogger.warn('SimpleFeed', 'Initial feed load taking >10s, attempting retry...');
        // Ação corretiva: tentar novamente
        fetchFeedDataRef.current();
      }
    }, 10000);

    return () => {
      clearTimeout(loadingTimeoutId);
    };
  }, [loading, profiles.length, user?.id]);

  return {
    profiles,
    userProfile,
    loading,
    error,
    getProfiles,
    updateConnectionStatus,
    getConnectionStatus,
    refetch: fetchFeedData,
    resetAndRefetch,
    loadMore,
    hasMore,
    loadedCount,
    totalCount: profiles.length
  };
};
